import {
  CreateDeliveryDto,
  KnownCreateDeliveryDtoChannel,
  KnownDeliveryDtoDeliveryType
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useMutationCreateDelivery } from '../../../deliveries.queries';
import { RestError } from '@azure/core-http';
import { useImmer } from 'use-immer';
import { castDraft } from 'immer';
import { v4 as uuidv4 } from 'uuid';
import { ReceiveDeliveryFormLayout, useReceiveDeliveryFormLayout } from './receive-delivery-form-layout.hook';
import {
  ReceiveDeliveryForm,
  ReceiveDeliveryFormConfig,
  useReceiveDeliveryFormConfig
} from './receive-delivery-form-config';

export interface ReceiveDeliveryActionProps {
  projectID: string;
  onAbort(): void;
  onSuccess(): void;
}

interface ReceiveDeliveryActionState {
  error?: RestError;
}

interface ReceiveDeliveryActionHookResponse {
  state: ReceiveDeliveryActionState;
  isLoading: boolean;
  form: UseFormReturn<ReceiveDeliveryForm>;
  formConfig: ReceiveDeliveryFormConfig;
  formLayout: ReceiveDeliveryFormLayout;
  onSubmitAndClose(form: ReceiveDeliveryForm): void;
  onSubmitAndKeepOpen(form: ReceiveDeliveryForm): void;
}

export function useReceiveDeliveryAction(props: ReceiveDeliveryActionProps): ReceiveDeliveryActionHookResponse {
  const [state, setState] = useImmer<ReceiveDeliveryActionState>({});
  // The saving state of the hook should not be exposed to the outside
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm<ReceiveDeliveryForm>({
    defaultValues: {
      projectID: props.projectID
    }
  });

  const { watch } = form;

  const projectID = watch('projectID');
  const deliveryType = watch('deliveryType');
  const hubID = watch('hubID');
  const deliveryContainerID = watch('deliveryContainerID');
  const deliveredByID = watch('deliveringParty');
  const deliveredToID = watch('receivingParty');

  // Reset form values if projectID or deliveryType changes
  useEffect(() => {
    form.setValue('hubID', '');
    form.setValue('deliveryContainerID', '');
    form.setValue('deliveringParty', '');
    form.setValue('receivingParty', '');
    form.setValue('carrierReference', '');
    form.clearErrors();
    setIsDefaultDeliveringPartySet(false);
  }, [projectID, deliveryType, form]);

  const { formConfig, formData } = useReceiveDeliveryFormConfig({
    projectID: props.projectID,
    selectedProjectID: projectID,
    selectedDeliveryType: deliveryType,
    selectedHubID: hubID
  });

  // Set the default project if it is not set
  useEffect(() => {
    if (!projectID && formData.defaults.project) {
      form.setValue('projectID', formData.defaults.project.value);
    }
  }, [projectID, form, formData.defaults.project]);

  // Set the default delivery type if it is not set
  useEffect(() => {
    if (!deliveryType && formData.defaults.deliveryType) {
      form.setValue('deliveryType', formData.defaults.deliveryType.value as KnownDeliveryDtoDeliveryType);
    }
  }, [deliveryType, form, formConfig.deliveryType.options, formData.defaults.deliveryType]);

  // Set the default hub if it is not set
  useEffect(() => {
    if (!hubID && formData.defaults.hub) {
      form.setValue('hubID', formData.defaults.hub.value);
    }
  }, [hubID, form, formData.defaults.hub]);

  // Set the default deliveryContainer if it is not set
  useEffect(() => {
    if (!deliveryContainerID && formData.defaults.deliveryContainer) {
      form.setValue('deliveryContainerID', formData.defaults.deliveryContainer.value);
    }
  }, [deliveryContainerID, form, formData.defaults.deliveryContainer]);

  const [isDefaultDeliveringPartySet, setIsDefaultDeliveringPartySet] = useState(false);

  // Set the default delivering party if it is not set
  useEffect(() => {
    if (formData.info.deliveringPartyType === 'provider' && !isDefaultDeliveringPartySet) {
      setIsDefaultDeliveringPartySet(true);
      if (deliveredByID) {
        return;
      }
      if (formData.defaults.deliveringParty) {
        form.setValue('deliveringParty', formData.defaults.deliveringParty.value);
      }
    }
  }, [
    isDefaultDeliveringPartySet,
    deliveredByID,
    deliveredToID,
    form,
    props.projectID,
    formData.info.deliveringPartyType,
    formData.defaults.deliveringParty
  ]);

  const mutation = useMutationCreateDelivery();

  function onSumbit(submittedForm: ReceiveDeliveryForm, keepOpen: boolean) {
    setIsSaving(true);

    const deliveryRequestID = uuidv4();

    const dto: CreateDeliveryDto = {
      channel: KnownCreateDeliveryDtoChannel.Cloud,
      deliveryRequestID,
      workflowID: deliveryRequestID,
      projectID: submittedForm.projectID,
      hubID: submittedForm.hubID,
      itemType: submittedForm.deliveryType,
      deliveredByID: submittedForm.deliveringParty,
      deliveredToID: submittedForm.receivingParty,
      deliveryContainerID: submittedForm.deliveryContainerID,
      carrierReference: submittedForm.carrierReference
    };

    mutation.mutate(dto, {
      onSuccess() {
        if (keepOpen) {
          form.setValue('carrierReference', '');
          form.setValue('deliveryContainerID', '');
          if (formData.info.deliveringPartyType === 'occupant') {
            form.setValue('deliveringParty', '');
          }
          if (formData.info.receivingPartyType === 'occupant') {
            form.setValue('receivingParty', '');
          }
          setIsSaving(false);
        } else {
          props.onSuccess();
        }
      },
      onError(error) {
        setState((draft) => {
          draft.error = castDraft(error);
        });
        setIsSaving(false);
      }
    });
  }

  function onSubmitAndClose(submittedForm: ReceiveDeliveryForm) {
    onSumbit(submittedForm, false);
  }

  function onSubmitAndKeepOpen(submittedForm: ReceiveDeliveryForm) {
    onSumbit(submittedForm, true);
  }

  const formLayout = useReceiveDeliveryFormLayout(deliveryType);

  const isLoading = formData.state.isLoading || isSaving;

  return {
    state,
    form,
    formConfig,
    formLayout,
    isLoading,
    onSubmitAndClose,
    onSubmitAndKeepOpen
  };
}
